import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DateTime } from 'luxon';

type TimerContextValue = {
  running: boolean;
  startTimer: () => void;
  stopTimer: () => void;
  startTime: DateTime | null;
  setStartTime: (startTime: DateTime | null) => void;
  disableTimer: boolean;
  setDisableTimer(canStartTimer: boolean): void;
  hideTimer?: boolean;
};

const TimerContext = React.createContext<TimerContextValue | null>(null);

export const useTimerContext = (): TimerContextValue => {
  const val = useContext(TimerContext);
  if (!val) {
    throw new Error(
      'useTimerContext must be called within a TimerContextProvider'
    );
  }

  return val;
};

type TimerContextProviderProps = {
  children: React.ReactNode;
  hideTimer?: boolean;
};

export const TimerContextProvider: React.FC<TimerContextProviderProps> = ({
  children,
  hideTimer = false
}: TimerContextProviderProps) => {
  const [running, setRunning] = useState<boolean>(false);
  const startTimer = useCallback(() => setRunning(true), []);
  const stopTimer = useCallback(() => setRunning(false), []);
  const [startTime, setStartTime] = useState<DateTime | null>(null);
  const [disableTimer, setDisableTimer] = useState<boolean>(false);
  return (
    <TimerContext.Provider
      value={useMemo(
        () => ({
          running,
          startTimer,
          stopTimer,
          startTime,
          setStartTime,
          disableTimer,
          setDisableTimer,
          hideTimer
        }),
        [disableTimer, running, startTime, startTimer, stopTimer, hideTimer]
      )}
    >
      {children}
    </TimerContext.Provider>
  );
};
